import React, { useEffect } from 'react'
import styled from 'styled-components'

import { Panel } from 'components/Panel'

import LastUpdated from 'pages/Device/LastUpdated'

import { highestSeverityForIcon, updateAlertsSeverity } from 'helpers/alerts'

import { color } from 'styles/colors'
import { NoData, screenSize } from 'styles/shared'

import useNexiaStream from 'hooks/useNexiaStream'
import { useWebsocketContext } from 'hooks/WebsocketContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import ExtendedDisconnect from 'pages/shared/Device/ExtendedDisconnect'

type Props = {
  deviceId: string
  reportCache: object
}

const Alerts = ({ deviceId, reportCache }: Props) => {
  const { eventData: report } = useNexiaStream(deviceId, 'alarms', reportCache)
  const { subscribe, unsubscribe, payload: alertsReport } = useWebsocketContext(deviceId, 'alerts')
  const { enabledFeatures } = useFeaturesContext()

  useEffect(() => {
    subscribe(deviceId, 'alerts')

    return () => {
      unsubscribe(deviceId, 'alerts')
    }
  }, [deviceId, subscribe, unsubscribe])

  const areWebsocketsEnabled = enabledFeatures.includes('montblanc_websockets')
  const activeReport = areWebsocketsEnabled ? alertsReport : report

  const alertsCount = activeReport?.alerts?.length || 0

  const extendedDisconnect = activeReport?.extendedDisconnect || false

  let severity = highestSeverityForIcon(updateAlertsSeverity(activeReport?.alerts || []))

  severity = severity?.includes('sda') ? severity.replace('-', '_') : severity

  const renderAlertsSeverityIcon = ({ severity }) => (severity ? <S.Img data-testid="alert-icon" aria-label={severity} alt={'Alerts'} src={`/img/${severity}_alert.svg`} /> : null)

  if (extendedDisconnect) {
    return (
      <ExtendedDisconnect title="Alerts" type="preview" />
    )
  }

  return (
    <S.Panel pingdomId="alerts-container">
      <Panel.Header>
        <Panel.Title>Alerts {activeReport ? `(${alertsCount})` : null}</Panel.Title>
        {
          !activeReport ? <S.NoData><h1>No Data Received Yet</h1></S.NoData>
            : (
              <>
                <LastUpdated date={activeReport?.lastUpdatedAt} />
                {renderAlertsSeverityIcon({ severity })}
              </>
            )
        }
      </Panel.Header>
    </S.Panel>
  )
}

export default Alerts

const S = {
  Panel: styled(Panel)`
    background-color: ${color('paneBackground')};
    border-bottom: 0px;
    padding: 0px;
    width: 100%;
    position: relative;
  `,
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  Img: styled.img`
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 58px;
    min-height: 58px;
    position: absolute;
    right: 10px;

    @media ${screenSize.small} {
      max-height: 48px;
      min-height: 48px;
      max-width: 125px;
    }
  `
}
